import http from './http';

// Base endpoint for billing-related APIs
const BASE_ENDPOINT = '/api/v1/billing';

// Unwrap helper to normalize API responses
const unwrap = (axiosResponse) => {
  if (!axiosResponse) return null;
  if (axiosResponse?.data === undefined) return axiosResponse;
  return axiosResponse.data?.data ?? axiosResponse.data;
};

const billingAPI = {
  /**
   * List invoices with optional filters/pagination
   */
  async listInvoices(params = {}) {
    const response = await http.get(`${BASE_ENDPOINT}/invoices`, { params });
    return unwrap(response);
  },
  /**
   * Streamlined Flow - Step 1 (GET): Fetch billing preview
   * @param {Object} params
   * @param {string} [params.appointmentId]
   * @param {string} [params.prescriptionId]
   * @param {string} [params.doctorId]
   */
  async getPreview(params = {}) {
    const response = await http.get(`${BASE_ENDPOINT}/preview`, { params });
    return unwrap(response);
  },

  /**
   * Streamlined Flow - Step 2 (POST): Create bill from selection
   * @param {Object} payload per backend createBillSchema
   */
  async createFromSelection(payload) {
    const response = await http.post(`${BASE_ENDPOINT}/create`, payload);
    return unwrap(response);
  },

  /**
   * Streamlined Flow - Step 3 (POST): Add payment to invoice
   * @param {string} invoiceId
   * @param {Object} payload per backend paymentSchema
   */
  async addPayment(invoiceId, payload) {
    if (!invoiceId) throw new Error('invoiceId is required');
    const response = await http.post(`${BASE_ENDPOINT}/invoices/${invoiceId}/payments`, payload);
    return unwrap(response);
  },

  /**
   * Get a comprehensive invoice preview by invoice ID
   * @param {string} invoiceId
   */
  async getInvoicePreviewById(invoiceId) {
    if (!invoiceId) throw new Error('invoiceId is required');
    const response = await http.get(`${BASE_ENDPOINT}/invoices/${invoiceId}/preview`);
    return unwrap(response);
  },

  /**
   * Get invoice by ID (fallback when preview API not available)
   */
  async getInvoiceById(invoiceId) {
    if (!invoiceId) throw new Error('invoiceId is required');
    const response = await http.get(`${BASE_ENDPOINT}/invoices/${invoiceId}`);
    return unwrap(response);
  },

  /**
   * Get doctor charges info to read doctor profile basics (fallback doctor details)
   */
  async getDoctorCharges(doctorId) {
    if (!doctorId) throw new Error('doctorId is required');
    const response = await http.get(`${BASE_ENDPOINT}/doctors/${doctorId}/charges`);
    return unwrap(response);
  },

  /**
   * Get active bank accounts for non-cash payments
   */
  async getBankAccounts(params = {}) {
    const response = await http.get(`${BASE_ENDPOINT}/bank-accounts`, { params });
    return unwrap(response);
  },
  /**
   * Create an invoice from a prescription
   * @param {Object} payload
   * @param {string} payload.prescriptionId - Source prescription id
   * @param {string} payload.patientId - Patient id
   * @param {string} [payload.appointmentId] - Optional appointment id
   * @param {string} [payload.billingType] - e.g. 'OPD', 'IPD'
   * @param {number} [payload.taxRate] - Tax percentage (e.g. 0, 5, 18)
   * @param {string} [payload.notes] - Any notes for the invoice
   */
  async createInvoiceFromPrescription(payload) {
    if (!payload?.prescriptionId) throw new Error('prescriptionId is required');
    if (!payload?.patientId) throw new Error('patientId is required');

    const response = await http.post(`${BASE_ENDPOINT}/invoices/from-prescription`, payload);
    return unwrap(response);
  },

  /**
   * Get billing statistics (total invoices, revenue, paid, pending, overdue)
   * @param {Object} params - { fromDate, toDate, billingType }
   */
  async getStats(params = {}) {
    const response = await http.get(`${BASE_ENDPOINT}/stats`, { params });
    return unwrap(response);
  },

  /**
   * Get doctor-wise revenue breakdown
   * @param {Object} params - { fromDate, toDate, doctorId }
   */
  async getDoctorRevenue(params = {}) {
    const response = await http.get(`${BASE_ENDPOINT}/doctor-revenue`, { params });
    return unwrap(response);
  },

  /**
   * Cancel an invoice
   * @param {string} invoiceId
   * @param {string} reason
   */
  async cancelInvoice(invoiceId, reason) {
    if (!invoiceId) throw new Error('invoiceId is required');
    const response = await http.put(`${BASE_ENDPOINT}/invoices/${invoiceId}/cancel`, { reason });
    return unwrap(response);
  },
};

export default billingAPI;
